import { NativeWorkspaceToolbar } from './NativeWorkspaceToolbar'
import { DevicePreviewDialog } from './DevicePreviewPanel'
import { activePreviewDevices } from './devicePreviewModel'
import { RehearsalDialog } from './RehearsalDialog'
import { rehearsalSummary } from './catalogSources'
import { ExperienceWorkspace } from './ExperienceWorkspace'
import { HomeNavigationInspector } from './HomeNavigationEditor'
import { navigationInventory } from './homeNavigationModel'
import { PREVIEW_CATALOG } from './nativePreviewModel'
import { useEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Link } from 'react-router'
import { FlaskConical, GitCompareArrows, History, MoreHorizontal, Redo2, Smartphone, Undo2 } from 'lucide-react'
import { Alert, Button, IconButton, Menu, PageHeader, PageLayout, Popover, StatePanel, StatusBadge, StatusBar } from '@/components/common'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from '@/hooks/useAuth'
import { PRODUCT_KEYS } from '@/config/studioProducts'
import { InspectorPanel, LayersPanel } from './NativeSportsbookEditor'
import { DeviceStage, NativePreview } from './NativePreview'
import { PublishControl, ReleasesDrawer } from './NativeSportsbookRelease'
import { NativeRevisionComparison } from './NativeRevisionComparison'
import { NativeClipboardControls } from './NativeClipboardControls'
import { NativeSectionBinding, NativeSectionLibrary } from './NativeReusableSections'
import { manifestStatusLabel, visiblePreviewComponents } from './nativeSportsbookManifest'
import { useNativeSportsbookWorkspace } from './useNativeSportsbookWorkspace'
import styles from './nativeSportsbook.module.css'
import workspaceLayout from './nativeWorkspace.module.css'

const STATUS_BADGE_TYPE = { draft: 'default', pending_approval: 'warning', approved: 'info', published: 'success', superseded: 'default' }

// The draft, selection, and dirty flag belong to one organization; keying the
// workspace by slug discards them on an organization switch instead of letting
// a retained draft save under the newly selected organization.
export default function NativeSportsbookPage() {
  const { currentOrg } = useOrganization()
  return <HomeWorkspace key={currentOrg?.slug ?? ''} slug={currentOrg?.slug} />
}

function HomeWorkspace({ slug }) {
  const { user } = useAuth()
  const { hasProductAccess } = useOrganization()
  const workspace = useNativeSportsbookWorkspace(slug)
  const [device, setDevice] = useState('ios')
  const [appearance, setAppearance] = useState(null)
  const surface = workspace.editorSelection.surface
  const [releasesOpen, setReleasesOpen] = useState(false)
  const [dialog, setDialog] = useState(null)
  const pairedPhones = activePreviewDevices(workspace).length
  const [issuesOpen, setIssuesOpen] = useState(false)
  const canConfigure = hasProductAccess(PRODUCT_KEYS.NATIVE_SPORTSBOOK, 'configure')
  const canPublish = hasProductAccess(PRODUCT_KEYS.NATIVE_SPORTSBOOK, 'publish')
  const canOperate = hasProductAccess(PRODUCT_KEYS.NATIVE_SPORTSBOOK, 'operate')
  const isAuthor = Boolean(user?.id && workspace.selected?.created_by === user.id)
  const previewCatalog = workspace.previewCatalog || PREVIEW_CATALOG
  const components = workspace.screen.components
  const visibleIds = new Set(visiblePreviewComponents(workspace.document, workspace.jurisdiction, workspace.sessionState, workspace.screen.id).map((component) => component.id))
  const requestError = workspace.requestError
  const { canUndo, canRedo, undo, redo, finishEditGroup } = workspace

  useEffect(() => {
    if (!canConfigure) return undefined
    const onKeyDown = (event) => {
      if (event.target?.closest?.('[role="dialog"]') || event.defaultPrevented || event.altKey || !(event.metaKey || event.ctrlKey) || event.target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return
      const key = event.key.toLowerCase()
      const redoRequested = (key === 'z' && event.shiftKey) || (key === 'y' && event.ctrlKey && !event.metaKey)
      if (redoRequested && canRedo) { event.preventDefault(); redo() }
      else if (key === 'z' && !event.shiftKey && canUndo) { event.preventDefault(); undo() }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('focusout', finishEditGroup)
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('focusout', finishEditGroup) }
  }, [canConfigure, canUndo, canRedo, undo, redo, finishEditGroup])

  const themeChip = workspace.currentTheme ? (
    <Link to="/dashboard/native-sportsbook/theme" className={workspaceLayout.identityMeta}>
      {workspace.themeOutdated
        ? <StatusBadge size="small" type="warning">Theme r{workspace.currentTheme.revision} available</StatusBadge>
        : `Theme r${workspace.selected?.theme_revision ?? workspace.currentTheme.revision}`}
    </Link>
  ) : null

  const identity = (
    <div className={workspaceLayout.identity}>
      {workspace.selected ? (
        <>
          <strong>Revision {workspace.selected.revision}</strong>
          <StatusBadge size="small" type={workspace.dirty ? 'warning' : (STATUS_BADGE_TYPE[workspace.selected.status] || 'default')}>
            {workspace.dirty ? 'Unsaved' : manifestStatusLabel(workspace.selected.status)}
          </StatusBadge>
        </>
      ) : <strong>New draft</strong>}
      {themeChip}
      <Button variant="ghost" size="compact" className={workspaceLayout.statusChip} onClick={() => setDialog('rehearse')}>{rehearsalSummary(workspace)}</Button>
      {pairedPhones ? <Button variant="ghost" size="compact" className={workspaceLayout.statusChip} onClick={() => setDialog('pair')}>{pairedPhones === 1 ? '1 phone paired' : `${pairedPhones} phones paired`}</Button> : null}
    </div>
  )

  const actions = (
    <div className={workspaceLayout.barActions}>
      <div className={workspaceLayout.barActions} role="group" aria-label="Edit history">
        <IconButton label="Undo" size="small" disabled={!canConfigure || !canUndo} onClick={undo}><Undo2 size={16} /></IconButton>
        <IconButton label="Redo" size="small" disabled={!canConfigure || !canRedo} onClick={redo}><Redo2 size={16} /></IconButton>
      </div>
      {workspace.validation && !workspace.validation.valid ? (
        <Popover
          placement="top-end"
          label="Validation issues"
          open={issuesOpen}
          onOpenChange={setIssuesOpen}
          trigger={<Button variant="ghost" size="compact">Issues ({workspace.validation.issues.length})</Button>}
        >
          <div className={styles.issuesList}>
            {workspace.validation.issues.map((issue, index) => (
              <div key={`${issue.path}-${issue.code}-${index}`}><span>{issue.message}</span><code>{issue.path}</code></div>
            ))}
          </div>
        </Popover>
      ) : null}
      <Button
        size="compact"
        variant={workspace.dirty || workspace.themeOutdated || !workspace.selected ? 'primary' : 'secondary'}
        disabled={!canConfigure || workspace.saveMutation.isPending}
        loading={workspace.saveMutation.isPending}
        onClick={() => workspace.saveMutation.mutate()}
      >
        {workspace.selected ? 'Save' : 'Save first draft'}
      </Button>
      <PublishControl workspace={workspace} canPublish={canPublish} isAuthor={isAuthor} />
      <Menu
        triggerLabel="More"
        trigger={<IconButton label="More" size="small"><MoreHorizontal size={16} aria-hidden="true" /></IconButton>}
        items={[
          { key: 'rehearse', label: 'Rehearse…', icon: FlaskConical, onSelect: () => setDialog('rehearse') },
          { key: 'pair', label: 'Pair devices…', icon: Smartphone, onSelect: () => setDialog('pair') },
          { key: 'compare', label: 'Compare changes…', icon: GitCompareArrows, onSelect: () => setDialog('compare') },
          { key: 'releases', label: 'Releases', icon: History, onSelect: () => setReleasesOpen(true) },
        ]}
      />
    </div>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <PageLayout size="workspace" className={styles.page}>
        <PageHeader title="App Builder" compact mode="workspace" />
        <NativeWorkspaceToolbar workspace={workspace} surface={surface} device={device} setDevice={setDevice} disabled={!canConfigure} />
        {workspace.manifestsQuery.isError ? <StatePanel state="error" title="Manifest workspace failed" description={workspace.manifestsQuery.error?.response?.data?.error || workspace.manifestsQuery.error?.message} onRetry={() => workspace.manifestsQuery.refetch()} compact /> : null}
        {requestError ? <Alert variant="error">{requestError?.response?.data?.error || requestError.message}</Alert> : null}
        {surface !== 'home' ? <ExperienceWorkspace workspace={workspace} organization={slug} surface={surface} device={device} appearance={appearance} disabled={!canConfigure} /> : <div className={styles.workspace}>
          <LayersPanel
            screen={workspace.screen}
            showNavigation={!workspace.screen.context}
            tools={<><NativeClipboardControls workspace={workspace} organization={slug} disabled={!canConfigure} /><NativeSectionLibrary workspace={workspace} slug={slug} disabled={!canConfigure} /></>}
            navigation={workspace.screen.navigation}
            components={components}
            registry={workspace.registry}
            selectedId={workspace.selectedComponentId}
            disabled={!canConfigure}
            onSelect={workspace.selectComponent}
            onMoveTo={workspace.moveComponentTo}
            onToggleVisible={(index, enabled) => workspace.updateComponent(index, { enabled })}
            onRemove={workspace.removeComponent}
            onDuplicate={workspace.duplicateComponent}
            onAdd={(type) => workspace.addComponent(type)}
            selectedCardId={workspace.selectedCardId}
            onSelectCard={workspace.selectCard}
            onAddCard={workspace.addCard}
            onMoveCardTo={workspace.moveCardTo}
            onRemoveCard={workspace.removeCard}
            onDuplicateCard={workspace.duplicateCard}
          />
          <div className={styles.canvasArea}>
            {workspace.liveCatalog?.isError ? <Alert variant="error">{workspace.liveCatalog.error.message}<Button variant="ghost" size="compact" onClick={() => workspace.liveCatalog.refetch()}>Retry</Button></Alert> : null}
            <DeviceStage>
              <NativePreview
                key={`${device}:${workspace.screen.id}`}
                screen={workspace.screen}
                onNavigateScreen={workspace.selectScreen}
                manifest={workspace.document}
                catalog={previewCatalog}
                components={components}
                visibleIds={visibleIds}
                device={device}
                sessionState={workspace.sessionState}
                selectedId={workspace.selectedComponentId}
                selectedCardId={workspace.selectedCardId}
                interactive={canConfigure}
                onSelect={workspace.selectComponent}
                onMoveTo={workspace.moveComponentTo}
                onInsert={(type, index) => workspace.addComponent(type, index)}
                onSelectCard={workspace.selectCard}
                onMoveCardTo={(componentId, from, to) => workspace.moveCardTo(components.findIndex((component) => component.id === componentId), from, to)}
                slug={slug}
                appearance={appearance}
                onAppearanceChange={setAppearance}
              />
            </DeviceStage>
          </div>
          {workspace.selectedComponentId?.startsWith('navigation:') ? <HomeNavigationInspector
            key={workspace.selectedComponentId}
            barKey={workspace.selectedComponentId.split(':')[1]}
            navigation={workspace.screen.navigation}
            events={navigationInventory(components.filter((component) => visibleIds.has(component.id)), previewCatalog.events, new Date(), previewCatalog.collection_results)}
            disabled={!canConfigure}
            onChange={workspace.updateNavigation}
          /> : <InspectorPanel
            screen={workspace.screen}
            selectedField={workspace.editorSelection.field}
            fieldSelectionId={workspace.editorSelection.fieldSelectionId}
            sectionControls={<NativeSectionBinding key={`reuse:${workspace.selectedComponentId}`} workspace={workspace} slug={slug} disabled={!canConfigure} />}
            slug={slug}
            catalog={previewCatalog}
            component={workspace.selectedComponent}
            index={workspace.selectedComponentIndex}
            count={components.length}
            disabled={!canConfigure}
            card={workspace.selectedCard}
            cardIndex={workspace.selectedCardIndex}
            onFieldChange={workspace.updateComponentField}
            onChange={workspace.updateComponent}
            onMove={workspace.moveComponent}
            onRemove={workspace.removeComponent}
            onCardFieldChange={workspace.updateCardField}
            onMoveCard={workspace.moveCardTo}
            onRemoveCard={workspace.removeCard}
            onSelectComponent={workspace.selectComponent}
          />}
        </div>}
        <StatusBar size="regular" label="App Builder status" start={identity} message={{ text: workspace.notice, tone: workspace.noticeTone }} end={actions} />
      </PageLayout>
      {dialog === 'rehearse' ? <RehearsalDialog workspace={workspace} onClose={() => setDialog(null)} /> : null}
      {dialog === 'pair' ? <DevicePreviewDialog workspace={workspace} canConfigure={canConfigure} onClose={() => setDialog(null)} /> : null}
      {dialog === 'compare' ? <NativeRevisionComparison workspace={workspace} onClose={() => setDialog(null)} /> : null}
      <ReleasesDrawer open={releasesOpen} onClose={() => setReleasesOpen(false)} workspace={workspace} canPublish={canPublish} canOperate={canOperate} registry={workspace.registry} />
    </DndProvider>
  )
}
